import { registerApplication } from 'single-spa';

import { ROUTES } from '../router/routes.config';

import { isRoleInPermissions } from '../router/utilities';

export const registerApplications = (GlobalStore, GlobalRouter, API, FLAGR_FEATURE_KEYS) => {
	const props = {
		GlobalStore,
		GlobalRouter,
		API,
	};

	const globalState = GlobalStore.getState();
	const isMeterEnabled = globalState.flagr.features[FLAGR_FEATURE_KEYS.NX_METER_ENABLED];
	const isPipelineV2Enabled =
		globalState.flagr.features[FLAGR_FEATURE_KEYS.NX_PIPELINES_APP_V2_ROLLOUT];
	const isTMV2Enabled =
		globalState.flagr.features[FLAGR_FEATURE_KEYS.NX_NEW_TRANSLATION_MEMORIES_APP];

	const applications = [
		{
			name: '@unbabel/topnav',
			activeWhen: (location) => true,
			props,
		},
		{
			name: '@unbabel/sidenav',
			activeWhen: (location) => true,
			props: {
				GlobalStore,
				GlobalRouter,
				API,
			},
		},
		{
			name: '@unbabel/overview',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = ROUTES.OVERVIEW;
				return [
					location.pathname === PATH,
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/reporting',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = isMeterEnabled ? ROUTES.REPORTING : ROUTES.REPORTING_USAGE;
				return [
					isMeterEnabled
						? location.pathname.startsWith(PATH.split(/\/:/)[0])
						: location.pathname === PATH,
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/manage-users',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = ROUTES.USERS;
				return [
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/configuration',
			activeWhen: (location) => {
				const { ACTIVE, PATH, PERMISSIONS } = ROUTES.INTEGRATIONS;
				return [
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/billing',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = ROUTES.BILLING;
				return [
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/languages',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = ROUTES.QUALITY_REPORTING;
				return [
					location.pathname.startsWith(PATH),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
					globalState.flagr.features[FLAGR_FEATURE_KEYS.NX_LANGUAGE_VISIBLE],
					!isMeterEnabled,
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/profile',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = ROUTES.PROFILE;
				return [
					location.pathname === PATH,
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/customers',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = ROUTES.CUSTOMERS;
				return [
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/subscriptions',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = ROUTES.SUBSCRIPTIONS;
				return [
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/file-upload',
			activeWhen: (location) => {
				const { ACTIVE, PATH, PERMISSIONS } = ROUTES.LANGUAGE_RESOURCES_FILE_UPLOAD;
				return [
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/glossaries',
			activeWhen: (location) => {
				const { ACTIVE, PATH, PERMISSIONS } = ROUTES.GLOSSARIES;
				return [
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/translations-auditing',
			activeWhen: (location) => {
				const { ACTIVE, PATH, PERMISSIONS } = ROUTES.TRANSLATIONS_AUDITING;
				return [
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/web-order',
			activeWhen: (location) => {
				const { PATH, PERMISSIONS } = ROUTES.PROJECTS;
				return [
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/quality-annotations',
			activeWhen: (location) => {
				const { ACTIVE, PATH, PERMISSIONS } = ROUTES.QUALITY_ANNOTATIONS;
				return [
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/playground',
			activeWhen: (location) => {
				const { ACTIVE, PATH, PERMISSIONS } = ROUTES.PIPELINES;
				return [
					!isPipelineV2Enabled,
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/pipelines',
			activeWhen: (location) => {
				const { ACTIVE, PATH, PERMISSIONS } = ROUTES.PIPELINES;
				return [
					isPipelineV2Enabled,
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/agents-reporting',
			activeWhen: (location) => {
				const { ACTIVE, PATH, PERMISSIONS } = ROUTES.AGENTS_REPORTING;
				return [
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
					globalState.flagr.features[FLAGR_FEATURE_KEYS.NX_AGENTS_REPORTING_VISIBLE],
					!isMeterEnabled,
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/translation-memories-old',
			activeWhen: (location) => {
				const { ACTIVE, PERMISSIONS, PATH } = ROUTES.TRANSLATION_MEMORIES;
				return [
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
		{
			name: '@unbabel/translation-memories',
			activeWhen: (location) => {
				const { ACTIVE, PERMISSIONS, PATH } = ROUTES.TRANSLATION_MEMORIES_NEW;
				return [
					isTMV2Enabled,
					ACTIVE,
					location.pathname.startsWith(PATH.split(/\/:/)[0]),
					isRoleInPermissions(globalState.user.roles, PERMISSIONS),
				].every(Boolean);
			},
			props,
		},
	];

	applications.map((app) => {
		const appModule = app.name;
		registerApplication(
			app.name,
			() => System.import(/* @vite-ignore */ appModule),

			app.activeWhen,
			app.props
		);
	});
};
